import { inject, Injectable, signal } from '@angular/core';
import { catchError, map, mergeMap, startWith, switchMap } from 'rxjs/operators';
import {
  AssistGeodataBM,
  ExchangeCodeResponse,
  ExchangeNavigationCodeResponse,
  FindPathResponseV2,
  FloorplanConfiguration,
  FloorplanLayoutEnum,
  NavigationConfiguration,
  NavigationConfigurationQueryParameters,
  NavigationDataCrate,
  NavigationDetailInfo,
  ResourceSchedule,
  NavigationStartInfo,
  PathInfoResultEnum,
  PathItem,
  StartTargetResultEnum,
  VirtualKeyboardButtonSize,
  VirtualKeyboardLayout,
} from '../models';
import { from, Observable, of, tap } from 'rxjs';
import { ApiService, NavigationGeodataObject, QRCategoryItemModel } from '@cue/api';
import { addMinutes } from 'date-fns';
import { TranslocoService } from '@jsverse/transloco';
import { localized } from '@cue/translate';

// db.ts
import Dexie, { Table } from 'dexie';
import { HttpClient } from '@angular/common/http';
import {
  AngleEnum,
  cuePackResource,
  getNextQuarter,
  InstructionItem,
  NavDirectionItem,
  NavigationBasicInfo,
  NavigationGeodataBM,
  NavigationNearestItem,
  NavigationResourceBasicInfo,
  NavigationResourceBasicInfoWithFirstLetter,
  Pack,
  PointInfo,
  ServerAliveResult,
  StartTargetSelectionModeEnum,
} from '@cue/data';

export class NavigationServiceDB extends Dexie {
  navConfigurations!: Table<any, string>;
  constructor() {
    super('navigation');
    this.version(3).stores({
      navConfigurations: 'id',
    });
  }
}

export const navigationServiceDB = new NavigationServiceDB();

export enum CalculatePathResponseType {
  Success = 0,
  StartIsTarget = 1,
  NotFound = 2,
  Error = 3,
}

export interface KioskConfigurationData {
  cacheKey?: string;
  kioskId?: string;
  configTimestamp: string;
}

export const standardKeyboardLayout = {
  firstRow: [
    {
      defaultValue: '+',
      ctrlValue: '`',
      shiftValue: '1',
      buttonSize: VirtualKeyboardButtonSize.Default,
    },
    {
      defaultValue: 'ě',
      ctrlValue: '@',
      shiftValue: '2',
      buttonSize: VirtualKeyboardButtonSize.Default,
    },
    {
      defaultValue: 'š',
      ctrlValue: '#',
      shiftValue: '3',
      buttonSize: VirtualKeyboardButtonSize.Default,
    },
    {
      defaultValue: 'č',
      ctrlValue: '$',
      shiftValue: '4',
      buttonSize: VirtualKeyboardButtonSize.Default,
    },
    {
      defaultValue: 'ř',
      ctrlValue: '~',
      shiftValue: '5',
      buttonSize: VirtualKeyboardButtonSize.Default,
    },
    {
      defaultValue: 'ž',
      ctrlValue: '^',
      shiftValue: '6',
      buttonSize: VirtualKeyboardButtonSize.Default,
    },
    {
      defaultValue: 'ý',
      ctrlValue: '&',
      shiftValue: '7',
      buttonSize: VirtualKeyboardButtonSize.Default,
    },
    {
      defaultValue: 'á',
      ctrlValue: '*',
      shiftValue: '8',
      buttonSize: VirtualKeyboardButtonSize.Default,
    },
    {
      defaultValue: 'í',
      ctrlValue: '{',
      shiftValue: '9',
      buttonSize: VirtualKeyboardButtonSize.Default,
    },
    {
      defaultValue: 'é',
      ctrlValue: '}',
      shiftValue: '0',
      buttonSize: VirtualKeyboardButtonSize.Default,
    },
    {
      defaultValue: '=',
      ctrlValue: '°',
      shiftValue: '%',
      buttonSize: VirtualKeyboardButtonSize.Default,
    },
    {
      defaultValue: "'",
      ctrlValue: '^',
      shiftValue: '',
      buttonSize: VirtualKeyboardButtonSize.Default,
    },
    {
      defaultValue: 'DEL',
      buttonSize: VirtualKeyboardButtonSize.Large,
      isDeleteFn: true,
    },
    {
      defaultValue: 'Close',
      buttonSize: VirtualKeyboardButtonSize.Large,
      isCloseFn: true,
    },
  ],
  secondRow: [
    {
      defaultValue: 'q',
      buttonSize: VirtualKeyboardButtonSize.Default,
    },
    {
      defaultValue: 'w',
      ctrlValue: 'ė',
      buttonSize: VirtualKeyboardButtonSize.Default,
    },
    {
      defaultValue: 'e',
      ctrlValue: 'ę',
      buttonSize: VirtualKeyboardButtonSize.Default,
    },
    {
      defaultValue: 'r',
      ctrlValue: '€',
      buttonSize: VirtualKeyboardButtonSize.Default,
    },
    {
      defaultValue: 't',
      buttonSize: VirtualKeyboardButtonSize.Default,
    },
    {
      defaultValue: 'z',
      ctrlValue: 'ż',
      buttonSize: VirtualKeyboardButtonSize.Default,
    },
    {
      defaultValue: 'u',
      buttonSize: VirtualKeyboardButtonSize.Default,
    },
    {
      defaultValue: 'i',
      buttonSize: VirtualKeyboardButtonSize.Default,
    },
    {
      defaultValue: 'o',
      buttonSize: VirtualKeyboardButtonSize.Default,
    },
    {
      defaultValue: 'p',
      buttonSize: VirtualKeyboardButtonSize.Default,
    },
    {
      defaultValue: 'ú',
      ctrlValue: '[',
      shiftValue: '/',
      buttonSize: VirtualKeyboardButtonSize.Default,
    },
    {
      defaultValue: ')',
      ctrlValue: ']',
      shiftValue: '(',
      buttonSize: VirtualKeyboardButtonSize.Default,
    },
  ],
  thirdRow: [
    {
      defaultValue: '',
      buttonSize: VirtualKeyboardButtonSize.ExtraLarge,
      isCapsLockFn: true,
      class: 'capslock',
    },
    {
      defaultValue: 'a',
      ctrlValue: 'ą',
      buttonSize: VirtualKeyboardButtonSize.Default,
    },
    {
      defaultValue: 's',
      ctrlValue: 'ė',
      buttonSize: VirtualKeyboardButtonSize.Default,
    },
    {
      defaultValue: 'd',
      ctrlValue: 'ę',
      buttonSize: VirtualKeyboardButtonSize.Default,
    },
    {
      defaultValue: 'f',
      ctrlValue: '€',
      buttonSize: VirtualKeyboardButtonSize.Default,
    },
    {
      defaultValue: 'g',
      buttonSize: VirtualKeyboardButtonSize.Default,
    },
    {
      defaultValue: 'h',
      ctrlValue: 'ż',
      buttonSize: VirtualKeyboardButtonSize.Default,
    },
    {
      defaultValue: 'j',
      buttonSize: VirtualKeyboardButtonSize.Default,
    },
    {
      defaultValue: 'k',
      buttonSize: VirtualKeyboardButtonSize.Default,
    },
    {
      defaultValue: 'l',
      buttonSize: VirtualKeyboardButtonSize.Default,
    },
    {
      defaultValue: 'ů',
      ctrlValue: '[',
      shiftValue: '/',
      buttonSize: VirtualKeyboardButtonSize.Default,
    },
    {
      defaultValue: ')',
      ctrlValue: ']',
      shiftValue: '(',
      buttonSize: VirtualKeyboardButtonSize.Default,
    },
    {
      defaultValue: '',
      buttonSize: VirtualKeyboardButtonSize.ExtraLarge,
      isEnterFn: true,
    },
  ],
  fourthRow: [
    {
      defaultValue: '',
      buttonSize: VirtualKeyboardButtonSize.Large,
      isShiftFn: true,
      class: 'shift',
    },
    {
      defaultValue: 'y',
      buttonSize: VirtualKeyboardButtonSize.Default,
    },
    {
      defaultValue: 'x',
      buttonSize: VirtualKeyboardButtonSize.Default,
    },
    {
      defaultValue: 'c',
      buttonSize: VirtualKeyboardButtonSize.Default,
    },
    {
      defaultValue: 'v',
      buttonSize: VirtualKeyboardButtonSize.Default,
    },
    {
      defaultValue: 'b',
      buttonSize: VirtualKeyboardButtonSize.Default,
    },
    {
      defaultValue: 'n',
      ctrlValue: '‚',
      buttonSize: VirtualKeyboardButtonSize.Default,
    },
    {
      defaultValue: 'm',
      buttonSize: VirtualKeyboardButtonSize.Default,
    },
    {
      defaultValue: ',',
      ctrlValue: '<',
      shiftValue: '?',
      buttonSize: VirtualKeyboardButtonSize.Default,
    },
    {
      defaultValue: '.',
      ctrlValue: '>',
      shiftValue: ':',
      buttonSize: VirtualKeyboardButtonSize.Default,
    },
    {
      defaultValue: '-',
      shiftValue: '_',
      buttonSize: VirtualKeyboardButtonSize.Default,
    },
  ],
  fifthRow: [
    {
      defaultValue: '',
      buttonSize: VirtualKeyboardButtonSize.ExtraLarge,
      isCtrlFn: true,
      class: 'special-chars',
    },
    {
      defaultValue: '',
      buttonSize: VirtualKeyboardButtonSize.ExtraLarge,
      isSpaceFn: true,
      class: 'spacebar',
    },
  ],
};

export const simpleKeyboardLayoutDefault = {
  firstRow: [
    {
      defaultValue: '1',
      ctrlValue: '1',
      buttonSize: VirtualKeyboardButtonSize.Default,
    },
    {
      defaultValue: '2',
      ctrlValue: '2',
      buttonSize: VirtualKeyboardButtonSize.Default,
    },
    {
      defaultValue: '3',
      ctrlValue: '3',
      buttonSize: VirtualKeyboardButtonSize.Default,
    },
    {
      defaultValue: '4',
      ctrlValue: '4',
      buttonSize: VirtualKeyboardButtonSize.Default,
    },
    {
      defaultValue: '5',
      ctrlValue: '5',
      buttonSize: VirtualKeyboardButtonSize.Default,
    },
    {
      defaultValue: '6',
      ctrlValue: '6',
      buttonSize: VirtualKeyboardButtonSize.Default,
    },
    {
      defaultValue: '7',
      ctrlValue: '7',
      buttonSize: VirtualKeyboardButtonSize.Default,
    },
    {
      defaultValue: '8',
      ctrlValue: '8',
      buttonSize: VirtualKeyboardButtonSize.Default,
    },
    {
      defaultValue: '9',
      ctrlValue: '9',
      buttonSize: VirtualKeyboardButtonSize.Default,
    },
    {
      defaultValue: '0',
      ctrlValue: '0',
      buttonSize: VirtualKeyboardButtonSize.Default,
    },
    {
      defaultValue: 'DEL',
      buttonSize: VirtualKeyboardButtonSize.Large,
      isDeleteFn: true,
    },
    {
      defaultValue: 'Close',
      buttonSize: VirtualKeyboardButtonSize.Large,
      isCloseFn: true,
    },
  ],
  secondRow: [
    {
      defaultValue: 'q',
      ctrlValue: '+',
      buttonSize: VirtualKeyboardButtonSize.Default,
    },
    {
      defaultValue: 'w',
      ctrlValue: '×',
      buttonSize: VirtualKeyboardButtonSize.Default,
    },
    {
      defaultValue: 'e',
      ctrlValue: '÷',
      buttonSize: VirtualKeyboardButtonSize.Default,
    },
    {
      defaultValue: 'r',
      ctrlValue: '=',
      buttonSize: VirtualKeyboardButtonSize.Default,
    },
    {
      defaultValue: 't',
      ctrlValue: '/',
      buttonSize: VirtualKeyboardButtonSize.Default,
    },
    {
      defaultValue: 'z',
      ctrlValue: '_',
      buttonSize: VirtualKeyboardButtonSize.Default,
    },
    {
      defaultValue: 'u',
      ctrlValue: '<',
      buttonSize: VirtualKeyboardButtonSize.Default,
    },
    {
      defaultValue: 'i',
      ctrlValue: '>',
      buttonSize: VirtualKeyboardButtonSize.Default,
    },
    {
      defaultValue: 'o',
      ctrlValue: '[',
      buttonSize: VirtualKeyboardButtonSize.Default,
    },
    {
      defaultValue: 'p',
      ctrlValue: ']',
      buttonSize: VirtualKeyboardButtonSize.Default,
    },
  ],
  thirdRow: [
    {
      defaultValue: '',
      buttonSize: VirtualKeyboardButtonSize.Large,
      isShiftFn: true,
      class: 'shift',
    },
    {
      defaultValue: 'a',
      ctrlValue: '!',
      buttonSize: VirtualKeyboardButtonSize.Default,
    },
    {
      defaultValue: 's',
      ctrlValue: '@',
      buttonSize: VirtualKeyboardButtonSize.Default,
    },
    {
      defaultValue: 'd',
      ctrlValue: '#',
      buttonSize: VirtualKeyboardButtonSize.Default,
    },
    {
      defaultValue: 'f',
      ctrlValue: '$',
      buttonSize: VirtualKeyboardButtonSize.Default,
    },
    {
      defaultValue: 'g',
      ctrlValue: '%',
      buttonSize: VirtualKeyboardButtonSize.Default,
    },
    {
      defaultValue: 'h',
      ctrlValue: '&',
      buttonSize: VirtualKeyboardButtonSize.Default,
    },
    {
      defaultValue: 'j',
      ctrlValue: '*',
      buttonSize: VirtualKeyboardButtonSize.Default,
    },
    {
      defaultValue: 'k',
      ctrlValue: '(',
      buttonSize: VirtualKeyboardButtonSize.Default,
    },
    {
      defaultValue: 'l',
      ctrlValue: ')',
      buttonSize: VirtualKeyboardButtonSize.Default,
    },
    {
      defaultValue: '',
      buttonSize: VirtualKeyboardButtonSize.ExtraLarge,
      isEnterFn: true,
    },
  ],
  fourthRow: [
    {
      defaultValue: '',
      buttonSize: VirtualKeyboardButtonSize.ExtraLarge,
      isCtrlFn: true,
      class: 'special-chars',
    },
    {
      defaultValue: 'y',
      ctrlValue: "'",
      buttonSize: VirtualKeyboardButtonSize.Default,
    },
    {
      defaultValue: 'x',
      ctrlValue: '"',
      buttonSize: VirtualKeyboardButtonSize.Default,
    },
    {
      defaultValue: 'c',
      ctrlValue: ':',
      buttonSize: VirtualKeyboardButtonSize.Default,
    },
    {
      defaultValue: 'v',
      ctrlValue: ';',
      buttonSize: VirtualKeyboardButtonSize.Default,
    },
    {
      defaultValue: 'b',
      ctrlValue: '|',
      buttonSize: VirtualKeyboardButtonSize.Default,
    },
    {
      defaultValue: 'n',
      ctrlValue: '?',
      buttonSize: VirtualKeyboardButtonSize.Default,
    },
    {
      defaultValue: 'm',
      ctrlValue: 'ß',
      buttonSize: VirtualKeyboardButtonSize.Default,
    },
    {
      defaultValue: ',',
      ctrlValue: 'Ş',
      buttonSize: VirtualKeyboardButtonSize.Default,
    },
    {
      defaultValue: '-',
      ctrlValue: 'ç',
      buttonSize: VirtualKeyboardButtonSize.Default,
    },
    {
      defaultValue: '.',
      ctrlValue: 'ñ',
      buttonSize: VirtualKeyboardButtonSize.Default,
    },
  ],
  fifthRow: [
    {
      defaultValue: '',
      buttonSize: VirtualKeyboardButtonSize.ExtraLarge,
      isSpaceFn: true,
      class: 'spacebar',
    },
  ],
};

export type HasAppId = {
  appId: string;
};
export type HasKioskId = {
  kioskId: string;
};
// Guard -
export function hasAppId(x: HasAppId | HasKioskId): x is HasAppId {
  return (x as HasAppId).appId !== undefined;
}
export function hasKioskId(x: HasAppId | HasKioskId): x is HasKioskId {
  return (x as HasKioskId).kioskId !== undefined;
}

const ROUTE_ID_KEY = 'routeId';
const NAV_KIOSK_CONFIG_LIST = 'navKioskConfigList';
const NAVIGATION_TAB_ID_KEY = 'navigationTabId';

@Injectable({
  providedIn: 'root',
})
export class NavigationService {
  private translocoService = inject(TranslocoService);
  private apiService = inject(ApiService);
  private httpService = inject(HttpClient);

  private getOrCreateNavigationTabId() {
    let tabId = sessionStorage.getItem(NAVIGATION_TAB_ID_KEY);
    if (tabId) {
      return tabId;
    }

    tabId = crypto.randomUUID();
    sessionStorage.setItem(NAVIGATION_TAB_ID_KEY, tabId);
    return tabId;
  }

  private getConfigurationCacheKey(scope: 'navigation' | 'floorplan', kioskOrAppId?: string) {
    if (kioskOrAppId) {
      return `${scope}:kiosk:${kioskOrAppId}`;
    }

    return `${scope}:web:${this.getOrCreateNavigationTabId()}`;
  }

  private getStoredConfigurationTimestamps() {
    const kioskConfigListFromStorage = localStorage.getItem(NAV_KIOSK_CONFIG_LIST);
    if (!kioskConfigListFromStorage) {
      return [] as KioskConfigurationData[];
    }

    try {
      const parsedData = JSON.parse(kioskConfigListFromStorage);
      return Array.isArray(parsedData) ? (parsedData as KioskConfigurationData[]) : [];
    } catch {
      return [];
    }
  }

  private getConfigurationTimestamp(cacheKey: string, legacyKioskId?: string) {
    const kioskConfigList = this.getStoredConfigurationTimestamps();
    return kioskConfigList.find(
      (x) => x.cacheKey === cacheKey || (legacyKioskId != null && x.kioskId === legacyKioskId)
    )?.configTimestamp;
  }

  private updateOrInsertKioskConfigTimestamp(cacheKey: string, timestamp: string, legacyKioskId?: string) {
    const kioskConfigList = this.getStoredConfigurationTimestamps();
    const kioskConfigUpdatedList = kioskConfigList.filter(
      (x) => x.cacheKey !== cacheKey && (legacyKioskId == null || x.kioskId !== legacyKioskId)
    );

    kioskConfigUpdatedList.push({
      cacheKey,
      configTimestamp: timestamp,
    });

    localStorage.setItem(NAV_KIOSK_CONFIG_LIST, JSON.stringify(kioskConfigUpdatedList));
  }

  private removeStoredConfigurationTimestamp(cacheKey: string, legacyKioskId?: string) {
    const kioskConfigList = this.getStoredConfigurationTimestamps();
    localStorage.setItem(
      NAV_KIOSK_CONFIG_LIST,
      JSON.stringify(
        kioskConfigList.filter((x) => x.cacheKey !== cacheKey && (legacyKioskId == null || x.kioskId !== legacyKioskId))
      )
    );
  }

  private getCachedConfigurationEntry(cacheKey: string, legacyKioskId?: string) {
    return navigationServiceDB.navConfigurations.get(cacheKey).then((cachedData) => {
      if (cachedData != null || legacyKioskId == null) {
        return cachedData;
      }

      return navigationServiceDB.navConfigurations.get(legacyKioskId);
    });
  }

  resetRouteId() {
    localStorage.setItem(ROUTE_ID_KEY, '');
  }

  resetNavConfigTimestampForKiosk(kioskId: string) {
    this.removeStoredConfigurationTimestamp(this.getConfigurationCacheKey('navigation', kioskId), kioskId);
  }

  getSchedulesRequest(params: { areaId: number } & (HasKioskId | HasAppId)) {
    let url = '/api/navigation/areas/' + params.areaId + '/schedules';
    const now = new Date();
    const start = getNextQuarter(now);
    const end = addMinutes(start, 15);
    url += '?start=' + start.toISOString();
    url += '&end=' + end.toISOString();

    if (hasAppId(params) && params.appId) {
      url += '&appId=' + params.appId;
    } else if (hasKioskId(params) && params.kioskId) {
      url += '&kioskId=' + params.kioskId;
    }

    return this.apiService.call<any, any>({ url: url.toString(), method: 'get' });
  }

  serverAvailable(): Observable<ServerAliveResult> {
    const url = '/api/navigation/checkapialive';
    return this.apiService.call<any, any>({ url: url.toString(), method: 'get' }).pipe(
      catchError((e) =>
        of({
          alive: false,
        })
      )
    );
  }

  resetNavigation(params: HasAppId | HasKioskId) {
    let url = '/api/navigation/reset';

    if (hasAppId(params) && params.appId) {
      url += `?appId=${params.appId}`;
    } else if (hasKioskId(params) && params.kioskId) {
      url += `?kioskId=${params.kioskId}`;
    }

    return this.apiService
      .call<any, any>({
        url: url,
        method: 'get',
      })
      .pipe(
        map((r) => {
          return true;
        })
      );
  }

  calculatePaths(pathResponse: FindPathResponseV2): {
    response: CalculatePathResponseType;
    directions: NavDirectionItem[];
  } {
    let currentShortestDirections: NavDirectionItem[] = [];
    let currentShortestDistance = Number.POSITIVE_INFINITY;

    const directions: NavDirectionItem[] = [];

    for (let i = 0; i < pathResponse.path.length; i++) {
      const areaRatio = pathResponse.areasInfo.find((x) => x.areaId === pathResponse.path[i].areaId).ratio;

      const pathA: PointInfo = {
        x: null,
        y: null,
        areaId: pathResponse.path[i].areaId,
        mapRatio: areaRatio,
        nextAreaId: i < pathResponse.path.length - 2 ? pathResponse.path[i + 1].areaId : null,
        parentPortal: null,
        childPortal: null,
      };

      const pathB: PointInfo = {
        x: null,
        y: null,
        areaId: pathResponse.path[i].areaId,
        mapRatio: areaRatio,
        nextAreaId: i < pathResponse.path.length - 2 ? pathResponse.path[i + 1].areaId : null,
        parentPortal: null,
        childPortal: null,
      };

      const pathC: PointInfo = {
        x: null,
        y: null,
        areaId: i != pathResponse.path.length - 1 ? pathResponse.path[i + 1].areaId : pathResponse.path[i].areaId,
        mapRatio: areaRatio,
        nextAreaId: null,
        parentPortal: null,
        childPortal: null,
      };

      //just 1 point to destination
      if (pathResponse.path.length === 1) {
        const destinationX = pathResponse.path[0].y;
        const destinationY = pathResponse.path[0].x;

        pathA.x = destinationX;
        pathA.y = destinationY;

        pathB.x = destinationX + 2;
        pathB.y = destinationY + 2;

        pathC.x = destinationX;
        pathC.y = destinationY;
      } else if (pathResponse.path.length >= 2) {
        pathA.x = pathResponse.path[i].y;
        pathA.y = pathResponse.path[i].x;

        if (i <= pathResponse.path.length - 2 && pathResponse.path[i].areaId == pathResponse.path[i + 1].areaId) {
          pathB.x = pathResponse.path[i + 1].y;
          pathB.y = pathResponse.path[i + 1].x;
        } else {
          pathB.x = pathA.x;
          pathB.y = pathA.y;
        }

        if (i < pathResponse.path.length - 3 && pathResponse.path[i].areaId == pathResponse.path[i + 1].areaId) {
          pathC.x = pathResponse.path[i + 1].y;
          pathC.y = pathResponse.path[i + 1].x;
        } else {
          pathC.x = pathA.x;
          pathC.y = pathA.y;
        }
      }

      const direction = {
        index: i,
        skip: false,
        skipNext: 0,
        first: pathA,
        second: pathB,
        third: pathC,
        nextAngleType: i < pathResponse.path.length - 2 ? pathResponse.path[i + 1].angleDirection : null,
        nextAngle: AngleEnum.Left,
        mapRatio: areaRatio,
        fiInfo: {
          fi: 0,
          angle: AngleEnum.Left,
          angleFi: 0,
        },
        distance: 0,
        startAreaId: pathA.areaId,
        endAreaId: pathC.areaId,
        message: null,
        areaMetric: null,
        parentPortal: null,
        childPortal: null,
        distanceRenew: 0,
        isPortal: pathResponse.path[i].isPortal,
        isLast: i === pathResponse.path.length - 1,
        // type: DirectionTypeEnum.Navigation,
        categoryName: pathResponse.path[i].categoryName,
        categoryIcon: pathResponse.path[i].categoryIcon,
        mapName: pathResponse.path[i].mapName,
        portalToPreviousAreaId: i > 0 ? pathResponse.path[i - 1].areaId : null,
        isHideInNavigation: pathResponse.path[i].hideInNavigation,
        nextPointIsHideInNavigation:
          i < pathResponse.path.length - 2 ? pathResponse.path[i + 1].hideInNavigation : null,
        previousPointIsHideInNavigation: i > 0 ? pathResponse.path[i - 1].hideInNavigation : null,
        zoomPOI: pathResponse.path[i].zoomPOI,
        poiSize: pathResponse.path[i].poiSize,
      };

      if (i > 0 && pathResponse.path[i - 1].areaId != pathResponse.path[i].areaId) {
        //the previous direction point was last in area
        //set to the point info about next new area (and to its last third subpoint too)
        // directions[i-1].newAreadId =pathResponse.path[i].areaId;
        // directions[i-1].third.areaId =pathResponse.path[i].areaId;
        directions[i - 1].isPortal = true;
      }

      if (i > 0) {
        directions[i - 1].distanceRenew = pathResponse.path[i].distanceToParent;
      }

      directions.push(direction as unknown as NavDirectionItem);
    }

    if (directions.length > 0) {
      directions[0].start = pathResponse.start;
      directions[directions.length - 1].target = pathResponse.target;

      const distanceTotall = directions.reduce((acc, it) => acc + it.distance, 0);
      if (distanceTotall < currentShortestDistance) {
        currentShortestDirections = directions;
        currentShortestDistance = distanceTotall;
      }
    }

    return {
      response:
        currentShortestDirections.length > 0 ? CalculatePathResponseType.Success : CalculatePathResponseType.NotFound,
      directions: currentShortestDirections,
    };
  }

  getCategoriesList(params: HasKioskId | HasAppId): Observable<Pack<QRCategoryItemModel[]>> {
    const url = '/api/navigation/categories-list';

    let payload = {};

    if (hasAppId(params) && params.appId) {
      payload = {
        ...payload,
        appId: params.appId,
      };
    } else if (hasKioskId(params) && params.kioskId) {
      payload = {
        ...payload,
        kioskId: params.kioskId,
      };
    }

    return this.apiService
      .call<any, QRCategoryItemModel[]>({
        url: url,
        method: 'post',
        payload,
      })
      .pipe(
        map((r) => ({
          loading: false,
          data: r.sort((a, b) => a.position - b.position).filter((x) => x.filterable),
          error: null,
        })),
        catchError((e) =>
          of({
            loading: false,
            error: e,
            data: [],
          })
        ),
        startWith({
          loading: true,
          data: [],
          error: null,
        })
      );
  }

  getScreenshot(screenshotId: string, lastDownloaded: string): Observable<any> {
    const url = `/api/unit/screenshot/${screenshotId}?lastDownloaded=${lastDownloaded ?? new Date().toISOString()}`;
    return this.httpService.get(this.apiService.getUrl(url), { responseType: 'blob' }).pipe(
      map((r) => {
        return r;
      })
    );
  }

  getNavigationDetailWithScreenshot(
    params: { resourceId?: number; qrCodeId?: string } & (HasAppId | HasKioskId)
  ): Observable<NavigationDetailInfo> {
    const navigationDetailBasicData = this.getNavigationDetail(params);

    return navigationDetailBasicData.pipe(
      switchMap((data) =>
        data?.unitInformation?.screenshotId
          ? this.getScreenshot(data.unitInformation.screenshotId, data.unitInformation.screenshotLastDownload).pipe(
              map((screenshot) => {
                const result = {
                  ...data,
                  unitInformation: {
                    ...data.unitInformation,
                    screenshotBlob: screenshot,
                  },
                };
                return result;
              })
            )
          : of(data)
      )
    );
  }

  getNavigationDetailForMultiview(
    params: {
      resourceId?: number;
      qrCodeId?: string;
    } & (HasAppId | HasKioskId)
  ): Observable<NavigationDetailInfo> {
    let url =
      params.resourceId != null
        ? `/api/assist/resources/${params.resourceId}/detail`
        : '/api/navigation/detail/qr/' + params.qrCodeId;

    const now = new Date();
    const start = getNextQuarter(now);
    const end = addMinutes(start, 15);
    url += '?start=' + start.toISOString() + '&end=' + end.toISOString();
    // TODO: abstrakce pro navigation komponenty, aby vedely kde jedou
    const token = localStorage.getItem('api_token');
    if (token) {
      url += '&includeDetail=true';
    }

    if (hasAppId(params) && params.appId) {
      url += '&appId=' + params.appId;
    } else if (hasKioskId(params) && params.kioskId) {
      url += '&kioskId=' + params.kioskId;
    }

    return this.apiService
      .call<any, NavigationDetailInfo>({
        url: url,
        method: 'get',
      })
      .pipe(
        map((r) => ({
          ...r,
          isResource: params.resourceId != null,
          objects: r.objects == null ? null : r.objects.sort((a, b) => b.rank - a.rank),
        }))
      );
  }

  getNavigationDetail(
    params: {
      resourceId?: number;
      qrCodeId?: string;
    } & (HasAppId | HasKioskId)
  ): Observable<NavigationDetailInfo> {
    let url =
      params.resourceId != null
        ? '/api/navigation/detail/resource/' + params.resourceId
        : '/api/navigation/detail/qr/' + params.qrCodeId;

    const now = new Date();
    const start = getNextQuarter(now);
    const end = addMinutes(start, 15);
    url += '?start=' + start.toISOString() + '&end=' + end.toISOString();
    // TODO: abstrakce pro navigation komponenty, aby vedely kde jedou
    const token = localStorage.getItem('api_token');
    if (token) {
      url += '&includeDetail=true';
    }

    if (hasAppId(params) && params.appId) {
      url += '&appId=' + params.appId;
    } else if (hasKioskId(params) && params.kioskId) {
      url += '&kioskId=' + params.kioskId;
    }

    return this.apiService
      .call<any, NavigationDetailInfo>({
        url: url,
        method: 'get',
      })
      .pipe(
        map((r) => ({
          ...r,
          isResource: params.resourceId != null,
          objects: r.objects == null ? null : r.objects.sort((a, b) => b.rank - a.rank),
        }))
      );
  }

  getNavigationSchedule(
    params: {
      resourceId: number;
    } & (HasAppId | HasKioskId)
  ): Observable<ResourceSchedule> {
    let url = '/api/navigation/detail/schedule/' + params.resourceId;

    const now = new Date();
    const start = getNextQuarter(now);
    const end = addMinutes(start, 15);
    url += '?start=' + start.toISOString() + '&end=' + end.toISOString();

    if (hasAppId(params) && params.appId) {
      url += '&appId=' + params.appId;
    } else if (hasKioskId(params) && params.kioskId) {
      url += '&kioskId=' + params.kioskId;
    }

    return this.apiService.call<any, ResourceSchedule>({
      url: url,
      method: 'get',
    });
  }

  getNavigationStartList(kioskId: string): Observable<Pack<NavigationStartInfo[]>> {
    const url = '/api/navigation/start-list';
    return this.apiService
      .call<any, NavigationStartInfo[]>({
        url: url,
        method: 'post',
        payload: {
          kioskId,
        },
      })
      .pipe(
        map((r) => ({
          loading: false,
          data: r
            .sort((a, b) => {
              const nameA =
                localized(a.displayNameForApp, this.translocoService) ?? localized(a.name, this.translocoService);
              const nameB =
                localized(b.displayNameForApp, this.translocoService) || localized(b.name, this.translocoService);

              if (nameA === null && nameB !== null) {
                return 1; // nameB pak nameA
              }
              if (nameA !== null && nameB === null) {
                return -1; // nameA pak nameB
              }
              if (nameA !== null && nameB !== null) {
                return nameA.localeCompare(nameB);
              }
              return 0;
            })
            .map((x) => {
              return {
                ...x,
                name: localized(x.displayNameForApp, this.translocoService) != null ? x.displayNameForApp : x.name,
                resourceGuid: x.resourceGuid,
              };
            }),
          error: null,
        })),
        catchError((e) =>
          of({
            loading: false,
            data: [],
            error: e,
          })
        ),
        startWith({
          loading: true,
          data: [],
          error: null,
        })
      );
  }

  getNavigationNearestPointList(
    params: {
      start:
        | {
            qrCodeId?: string;
            resourceId?: number;
          }
        | undefined;
      categoryIdList: string[];
      areaIdList: number[];
      resourceTypeIdList: number[];
    } & (HasAppId | HasKioskId)
  ): Observable<NavigationNearestItem[]> {
    const url = '/api/navigation/nearest-item-list';

    let payload: any = {
      resourceTypeList: params.resourceTypeIdList,
      categoryList: params.categoryIdList,
      areaList: params.areaIdList,
      start: {
        startQrCodeId: params.start?.qrCodeId,
        startResourceId: params.start?.resourceId,
      },
    };

    if (hasAppId(params) && params.appId) {
      payload = {
        ...payload,
        appId: params.appId,
      };
    } else if (hasKioskId(params) && params.kioskId) {
      payload = {
        ...payload,
        kioskId: params.kioskId,
      };
    }

    return this.apiService
      .call<any, { nearestItemList: NavigationNearestItem[] }>({
        url: url,
        method: 'post',
        payload: payload,
      })
      .pipe(
        map((r) =>
          r.nearestItemList
            .sort((a, b) => {
              const minDistanceA = Math.min(...a.qrCodes.map((qrCode) => qrCode.distance));
              const minDistanceB = Math.min(...b.qrCodes.map((qrCode) => qrCode.distance));

              return minDistanceA - minDistanceB;
            })
            .map((x) => {
              return {
                ...x,
                breadcrumbWithIds:
                  x.breadcrumbWithIds.length === 1 ? x.breadcrumbWithIds : x.breadcrumbWithIds.slice(1),
              };
            })
        )
      );
  }

  loadResourceNavigationList(params: any): Observable<Pack<NavigationResourceBasicInfoWithFirstLetter[]>> {
    const url = '/api/FloorplanApp/list';
    const payload = {
      appId: params.appId,
      deploymentId: null,
      kioskId: null,
      languageCode: params.languageCode,
      searchString: params.searchString,
      selectedAreaIds: [],
      selectedCategoryIdList: [],
      selectedResourceTypeIds: [],
      skip: 0,
      take: 50,
    };

    return this.apiService
      .call<any, any>({
        url: url,
        method: 'post',
        payload,
      })
      .pipe(
        map((r) => {
          return {
            ...r,
            data: r.data.map((item) => {
              return {
                ...item,
                eventData: item.eventData.map((eventData) => {
                  return {
                    ...eventData,
                    realStart: new Date(eventData.realStart),
                    realEnd: new Date(eventData.realEnd),
                  };
                }),
              };
            }),
          };
        })
      );
  }

  getResourceNavigationListFakeData(appId: string): Observable<Pack<any[]>> {
    return of({
      data: [
        {
          name: 'Zasedací místnost Alpha',
          displayNameForApp: 'Alpha',
          resourceId: 1,
          resourceGuid: 'res-alpha-001',
          reservable: true,
          location: [],
          areaId: 10,
          file: undefined,
          icon: 'meeting_room',
          iconPreferCover: false,
          iconObjectPosition: 'center',
          imagePreferCover: false,
          imageObjectPosition: 'center',
          fileExtension: undefined,
          distance: 120,
          qrCodes: [],
          qrCodeId: undefined,
          resourceTypeId: 1,
          resourceTypeName: 'Meeting room',
          resourceDescription: 'Velká zasedací místnost s projektorem',
          resourcePropertiesInfo: [],
          tags: [],
        },
        {
          name: 'Auto Škoda Octavia',
          displayNameForApp: 'Octavia',
          resourceId: 2,
          resourceGuid: 'res-car-002',
          reservable: true,
          location: [],
          areaId: 20,
          file: undefined,
          icon: 'directions_car',
          iconPreferCover: true,
          iconObjectPosition: 'center',
          imagePreferCover: true,
          imageObjectPosition: 'center',
          fileExtension: undefined,
          distance: 450,
          qrCodes: [],
          qrCodeId: undefined,
          resourceTypeId: 2,
          resourceTypeName: 'Vehicle',
          resourceDescription: 'Firemní automobil pro služební cesty',
          resourcePropertiesInfo: [],
          tags: [],
        },
        {
          name: 'Projektor Epson',
          displayNameForApp: 'Projektor',
          resourceId: 3,
          resourceGuid: 'res-projector-003',
          reservable: false,
          location: [],
          areaId: 10,
          file: undefined,
          icon: 'videocam',
          iconPreferCover: false,
          iconObjectPosition: 'center',
          imagePreferCover: false,
          imageObjectPosition: 'center',
          fileExtension: undefined,
          distance: undefined,
          qrCodes: [],
          qrCodeId: undefined,
          resourceTypeId: 3,
          resourceTypeName: 'Equipment',
          resourceDescription: 'Přenosný projektor',
          resourcePropertiesInfo: [],
          tags: [],
        },
        {
          name: 'Společenská místnost',
          displayNameForApp: 'Lounge',
          resourceId: 4,
          resourceGuid: 'res-lounge-004',
          reservable: true,
          location: [],
          areaId: 30,
          file: undefined,
          icon: 'weekend',
          iconPreferCover: true,
          iconObjectPosition: 'center',
          imagePreferCover: true,
          imageObjectPosition: 'center',
          fileExtension: undefined,
          distance: 80,
          qrCodes: [],
          qrCodeId: undefined,
          resourceTypeId: 1,
          resourceTypeName: 'Room',
          resourceDescription: 'Místnost pro neformální setkání',
          resourcePropertiesInfo: [],
          tags: [],
        },
        {
          name: 'Tiskárna HP LaserJet',
          displayNameForApp: 'Tiskárna',
          resourceId: 5,
          resourceGuid: 'res-printer-005',
          reservable: false,
          location: [],
          areaId: 40,
          file: undefined,
          icon: 'print',
          iconPreferCover: false,
          iconObjectPosition: 'center',
          imagePreferCover: false,
          imageObjectPosition: 'center',
          fileExtension: undefined,
          distance: undefined,
          qrCodes: [],
          qrCodeId: undefined,
          resourceTypeId: 3,
          resourceTypeName: 'Equipment',
          resourceDescription: 'Kancelářská laserová tiskárna',
          resourcePropertiesInfo: [],
          tags: [],
        },
      ],
      loading: false,
    });
  }

  getNavigationBasicList(appId: string): Observable<Pack<NavigationBasicInfo[]>> {
    const url = '/api/navigation/basic-list-v2';

    // languageCode: 'en';
    // searchString: '';
    // selectedAreaIds: [];
    // selectedCategoryIdList: [];
    // selectedResourceTypeIds: [];
    // skip: 0;
    // take: 0;

    return this.apiService
      .call<any, NavigationBasicInfo[]>({
        url: url,
        method: 'post',
        payload: {
          appId,
          languageCode: 'en',
          searchString: '',
          selectedAreaIds: [],
          selectedCategoryIdList: [],
          selectedResourceTypeIds: [],
          skip: 0,
          take: 0,
        },
      })
      .pipe(
        map((r) => ({
          loading: false,
          data: r
            .sort((a, b) => {
              const nameA =
                localized(a.displayNameForApp, this.translocoService) || localized(a.name, this.translocoService);
              const nameB =
                localized(b.displayNameForApp, this.translocoService) || localized(b.name, this.translocoService);

              if (nameA === null && nameB !== null) {
                return 1; // nameB pak nameA
              }
              if (nameA !== null && nameB === null) {
                return -1; // nameA pak nameB
              }
              if (nameA !== null && nameB !== null) {
                return nameA.localeCompare(nameB);
              }
              return 0;
            })
            .map((x) => {
              return {
                ...x,
                name: localized(x.displayNameForApp, this.translocoService) != null ? x.displayNameForApp : x.name,
              };
            }),
          error: null,
        })),
        catchError((e) =>
          of({
            loading: false,
            data: [],
            error: e,
          })
        ),
        startWith({
          loading: true,
          data: [],
          error: null,
        })
      );
  }

  maxCodeLength(): Observable<Pack<number>> {
    const url = '/api/navigation/code-length';
    return this.apiService
      .call<any, number>({
        url: url,
        method: 'get',
      })
      .pipe(
        map((r) => ({ loading: false, data: r, error: null })),
        startWith({ loading: true, data: null, error: null }),
        catchError((e) =>
          of({
            loading: false,
            data: null,
            error: e,
          })
        )
      );
  }

  getNavGeodataForAreaId(
    params: { areaId: number; deploymentId: string } & (HasAppId | HasKioskId)
  ): Observable<Pack<NavigationGeodataBM[]>> {
    const url = '/api/navigation/areas/navgeodata';

    let payload: any = {
      areaId: params.areaId,
      deploymentId: params.deploymentId,
    };

    if (hasAppId(params) && params.appId) {
      payload = {
        ...payload,
        appId: params.appId,
      };
    } else if (hasKioskId(params) && params.kioskId) {
      payload = {
        ...payload,
        kioskId: params.kioskId,
      };
    }

    return this.apiService
      .call<any, NavigationGeodataBM[]>({
        url: url,
        method: 'post',
        payload,
      })
      .pipe(
        map((r) => ({
          loading: false,
          data: r.map((x) => {
            return {
              ...x,
              areaId: params.areaId,
            };
          }),
          error: null,
        })),
        startWith({ loading: true, data: [], error: null }),
        catchError((e) =>
          of({
            loading: false,
            data: [],
            error: e,
          })
        )
      );
  }

  getNavGeodataUnitObjectsForAreaId(
    params: { areaId: number; deploymentId: string } & (HasAppId | HasKioskId)
  ): Observable<Pack<NavigationGeodataObject[]>> {
    const url = '/api/navigation/areas/navgeodataUnitObjects';

    let payload: any = {
      areaId: params.areaId,
      deploymentId: params.deploymentId,
    };

    if (hasAppId(params) && params.appId) {
      payload = {
        ...payload,
        appId: params.appId,
      };
    } else if (hasKioskId(params) && params.kioskId) {
      payload = {
        ...payload,
        kioskId: params.kioskId,
      };
    }

    return this.apiService
      .call<any, NavigationGeodataObject[]>({
        url: url,
        method: 'post',
        payload,
      })
      .pipe(
        map((r) => ({
          loading: false,
          data: r.map((x) => {
            return {
              ...x,
              areaId: +params.areaId,
            };
          }),
          error: null,
        })),
        startWith({ loading: true, data: [], error: null }),
        catchError((e) =>
          of({
            loading: false,
            data: [],
            error: e,
          })
        )
      );
  }

  getGeodataForAreaId(areaId: number): Observable<Pack<AssistGeodataBM[]>> {
    const url = '/api/navigation/areas/' + areaId + '/geodata';

    return this.apiService.call<any, AssistGeodataBM[]>({ url: url, method: 'get' }).pipe(
      map((r) => ({ loading: false, data: r, error: null })),
      startWith({ loading: true, data: [], error: null }),
      catchError((e) =>
        of({
          loading: false,
          data: [],
          error: e,
        })
      )
    );
  }

  exchangeNavigationGUID(id: string): Observable<Pack<ExchangeNavigationCodeResponse>> {
    const url = '/api/navigation/exchange-guid-nav/' + id;
    return this.apiService.call<any, ExchangeNavigationCodeResponse>({ url: url, method: 'get' }).pipe(
      map((r) => ({ loading: false, data: r, error: null })),
      startWith({ loading: true, data: null, error: null }),
      catchError((e) =>
        of({
          loading: false,
          data: null,
          error: e,
        })
      )
    );
  }

  exchangeGUID(id: string): Observable<Pack<ExchangeCodeResponse>> {
    const url = '/api/navigation/exchange-guid/' + id;
    return this.apiService.call<any, ExchangeCodeResponse>({ url: url, method: 'get' }).pipe(
      map((r) => ({ loading: false, data: r, error: null })),
      startWith({ loading: true, data: null, error: null }),
      catchError((e) =>
        of({
          loading: false,
          data: null,
          error: e,
        })
      )
    );
  }

  exchangeCode(code: number): Observable<Pack<ExchangeCodeResponse>> {
    const url = '/api/navigation/exchange-code/' + code;
    return this.apiService.call<any, ExchangeCodeResponse>({ url: url, method: 'get' }).pipe(
      map((r) => ({ loading: false, data: r, error: null })),
      startWith({ loading: true, data: null, error: null }),
      catchError((e) =>
        of({
          loading: false,
          data: null,
          error: e,
        })
      )
    );
  }

  getKeyboardLayout(langCode: string): VirtualKeyboardLayout {
    return simpleKeyboardLayoutDefault;
    // return simpleKeyboardLayout;
    // return standardKeyboardLayout;
  }

  getCorrectedUrl(
    params: NavigationConfigurationQueryParameters,
    appId?: string
  ): Observable<NavigationConfigurationQueryParameters> {
    let finalParams: any = params;

    if (appId) {
      finalParams = {
        ...finalParams,
        appId,
      };
    }

    const url = '/api/navigation/url-correction';
    return this.apiService.call<any, any>({
      url: url,
      method: 'post',
      payload: finalParams,
    });
  }

  private normalizeAreaDisableRotation(area: any) {
    return {
      ...area,
      disableRotation: area?.disableRotation ?? area?.DisableRotation ?? false,
    };
  }

  private normalizeAreasInfoData(areasInfoData?: any[]) {
    return (areasInfoData ?? []).map((area) => this.normalizeAreaDisableRotation(area));
  }

  private normalizeAreasInfo(areasInfo: any[] | undefined, areasInfoData: any[]) {
    return (areasInfo ?? []).map((areaInfo) => {
      const normalizedAreaInfo = this.normalizeAreaDisableRotation(areaInfo);

      return {
        ...normalizedAreaInfo,
        breadcrumbWithIds: (areaInfo.breadcrumbWithIds ?? []).map((x) => ({
          areaId: x.item1 ?? x.areaId,
          name: x.item2 ?? x.name,
        })),
        newFloorplanWidth: areasInfoData.find((a) => a.id === areaInfo.areaId)?.newFloorplanWidth ?? 0,
        newFloorplanHeight: areasInfoData.find((a) => a.id === areaInfo.areaId)?.newFloorplanHeight ?? 0,
      };
    });
  }

  private normalizeNavigationConfigurationPayload<T extends { areasInfo?: any[]; areasInfoData?: any[] }>(
    payload: T
  ): T {
    const normalizedAreasInfoData = this.normalizeAreasInfoData(payload?.areasInfoData);
    return {
      ...payload,
      areasInfoData: normalizedAreasInfoData,
      areasInfo: this.normalizeAreasInfo(payload?.areasInfo, normalizedAreasInfoData),
    } as T;
  }

  getFloorplanConfiguration(appId: string, deploymentId: string): Observable<Pack<FloorplanConfiguration>> {
    const url = '/api/FloorplanApp/Configuration';
    const cacheKey = this.getConfigurationCacheKey('floorplan', appId);

    const notValidKioskData: Pack<FloorplanConfiguration> = {
      data: {
        validFloorplanApp: false,
        canNavigateToQRCode: false,
        kioskId: null,
        isKiosk: false,
        qrCodeId: null,
        areasInfo: [],
        name: null,
        defaultAreaId: null,
        areasInfoData: [],
        enable: false,
        digitalSignageConfig: null,
        resetAfterCountdownShow: null,
        showCountdown: null,
        textForScanQrCode: null,
        isCachedData: false,
        outOfOrder: false,
        outOfOrderText: '',
        showVirtualKeyboard: false,
        layout: FloorplanLayoutEnum.Navigation,
        unitDisplayAndLEDSetting: null,
      },
      loading: false,
    };

    if (appId && !this.checkIsGuid(appId)) {
      console.error('NOT VALID KIOSK GUID!!!');
      return of(notValidKioskData);
    }

    const cachedConfiguration$ = from(this.getCachedConfigurationEntry(cacheKey, appId)) as Observable<
      Pack<FloorplanConfiguration>
    >;

    const payload: any = {
      deploymentId,
      appId,
    };

    const storedTimestamp = this.getConfigurationTimestamp(cacheKey, appId);
    if (storedTimestamp) {
      payload.timestamp = storedTimestamp;
    }

    const serverData$ = this.apiService
      .call<any, any>({
        url: url,
        method: 'post',
        payload: payload,
      })
      .pipe(
        tap((x) => {
          if (x?.timestamp) {
            this.updateOrInsertKioskConfigTimestamp(cacheKey, x.timestamp, appId);
            navigationServiceDB.navConfigurations.put({
              id: cacheKey,
              data: this.normalizeNavigationConfigurationPayload({
                ...x,
                kioskId: appId,
              }),
              loading: false,
            });
          }
        }),
        map((r) => {
          return {
            loading: false,
            data: this.normalizeNavigationConfigurationPayload({
              ...r,
              isKiosk: appId,
              kioskId: appId ?? null,
              isCachedData: r === null,
            }),
            error: null,
          };
        }),
        catchError((e) =>
          of({
            loading: false,
            data: null,
            error: e,
          })
        )
      );

    return cachedConfiguration$.pipe(
      mergeMap((cachedData) => {
        return serverData$.pipe(
          map((serverData) => {
            if (serverData.data?.isCachedData === false) {
              return {
                data: {
                  ...serverData.data,
                  isCachedData: false,
                },
              };
            }
            if (cachedData?.data != null) {
              return {
                ...cachedData,
                data: {
                  ...this.normalizeNavigationConfigurationPayload(cachedData.data),
                  isCachedData: true,
                },
              };
            } else {
              return notValidKioskData;
            }
          })
        );
      })
    );
  }

  getConfiguration(kioskId: string, deploymentId: string): Observable<Pack<NavigationConfiguration>> {
    const url = '/api/navigation/Configuration';
    const cacheKey = this.getConfigurationCacheKey('navigation', kioskId);

    const notValidKioskData: Pack<NavigationConfiguration> = {
      data: {
        validFloorplanApp: false,
        canNavigateToQRCode: false,
        kioskId: null,
        isKiosk: false,
        qrCodeId: null,
        areasInfo: [],
        name: null,
        defaultAreaId: null,
        areasInfoData: [],
        enable: false,
        digitalSignageConfig: null,
        resetAfterCountdownShow: null,
        showCountdown: null,
        textForScanQrCode: null,
        isCachedData: false,
        outOfOrder: false,
        outOfOrderText: '',
        showVirtualKeyboard: false,
      },
      loading: false,
    };

    if (kioskId && !this.checkIsGuid(kioskId)) {
      console.error('NOT VALID KIOSK GUID!!!');
      return of(notValidKioskData);
    }

    const cachedConfiguration$ = from(this.getCachedConfigurationEntry(cacheKey, kioskId)) as Observable<
      Pack<NavigationConfiguration>
    >;

    const payload: any = {
      deploymentId,
      kioskId,
    };

    const storedTimestamp = this.getConfigurationTimestamp(cacheKey, kioskId);
    if (storedTimestamp) {
      payload.timestamp = storedTimestamp;
    }

    const serverData$ = this.apiService
      .call<any, any>({
        url: url,
        method: 'post',
        payload: payload,
      })
      .pipe(
        tap((x) => {
          if (x?.timestamp) {
            this.updateOrInsertKioskConfigTimestamp(cacheKey, x.timestamp, kioskId);
            navigationServiceDB.navConfigurations.put({
              id: cacheKey,
              data: this.normalizeNavigationConfigurationPayload({
                ...x,
                kioskId: kioskId,
              }),
              loading: false,
            });
          }
        }),
        map((r) => {
          return {
            loading: false,
            data: this.normalizeNavigationConfigurationPayload({
              ...r,
              isKiosk: kioskId,
              kioskId: kioskId ?? null,
              isCachedData: r === null,
            }),
            error: null,
          };
        }),
        catchError((e) =>
          of({
            loading: false,
            data: null,
            error: e,
          })
        )
      );

    return cachedConfiguration$.pipe(
      mergeMap((cachedData) => {
        return serverData$.pipe(
          map((serverData) => {
            if (serverData.data?.isCachedData === false) {
              return {
                data: {
                  ...serverData.data,
                  isCachedData: false,
                },
              };
            }
            if (cachedData?.data != null) {
              return {
                ...cachedData,
                data: {
                  ...this.normalizeNavigationConfigurationPayload(cachedData.data),
                  isCachedData: true,
                },
              };
            } else {
              return notValidKioskData;
            }
          })
        );
      })
    );
  }

  private checkIsGuid(guid: string): boolean {
    const GUIDRegExp = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
    return GUIDRegExp.test(guid);
  }

  private generateNotSuccessRouteResult(
    startResult?: StartTargetResultEnum,
    targetResult?: StartTargetResultEnum
  ): Observable<Pack<NavigationDataCrate>> {
    if (startResult) {
      console.error('NOT VALID START GUID!!!');
    }
    if (targetResult) {
      console.error('NOT VALID TARGET GUID!!!');
    }

    return of({
      loading: false,
      data: {
        directions: [],
        response: {
          path: [],
          pathResult: {
            path: PathInfoResultEnum.PathNotFound,
            target: targetResult ?? StartTargetResultEnum.NotRequested,
            start: startResult ?? StartTargetResultEnum.NotRequested,
          },
          areasInfo: [],
          pathInstructions: [],
          targetIsResource: false,
          barrierFree: false,
          start: null,
          target: null,
          targetGUID: null,
          targetName: null,
          targetResourceId: null,
        },
        responseType: CalculatePathResponseType.Error,
      },
    });
  }

  private allPathItemsIsHideInNavigation(pathList: PathItem[], areaPathIndexList: InstructionItem[]): boolean {
    for (const item of areaPathIndexList) {
      const allPointsInNavStepIsHideInNavigation = item.points.map((x) => pathList[x].hideInNavigation).every((x) => x);
      if (!allPointsInNavStepIsHideInNavigation) {
        return false;
      }
    }

    return true;
  }

  findPath(
    params: {
      startQrCodeId: string;
      startResourceId: number;
      target: {
        targetQrCodeId?: string;
        targetResourceId?: number;
      };
      wantOnlyBarrierFreePaths: boolean;
      startSelectionMode: StartTargetSelectionModeEnum;
      targetSelectionMode: StartTargetSelectionModeEnum;
      currentUrl: string;
    } & (HasAppId | HasKioskId)
  ): Observable<Pack<NavigationDataCrate>> {
    const url = '/api/navigation/find-path-info';

    let payload: any = {
      barrierFree: params.wantOnlyBarrierFreePaths,
      actuallUrl: params.currentUrl,
    };

    if (hasAppId(params) && params.appId) {
      payload = {
        ...payload,
        appId: params.appId,
      };
    }

    if (params.startSelectionMode != null) {
      payload.startType = params.startSelectionMode;
    }

    if (params.targetSelectionMode != null) {
      payload.targetType = params.targetSelectionMode;
    }

    let startSelected = false;
    let targetSelected = false;

    if (params.startQrCodeId) {
      if (!this.checkIsGuid(params.startQrCodeId)) {
        return this.generateNotSuccessRouteResult(StartTargetResultEnum.NotFound, null);
      }
      startSelected = true;
      payload.startQrCodeId = params.startQrCodeId;
    }
    if (params.startResourceId && params.startQrCodeId == null) {
      if (isNaN(params.startResourceId)) {
        return this.generateNotSuccessRouteResult(StartTargetResultEnum.NotFound, null);
      }
      startSelected = true;
      payload.startResourceId = params.startResourceId;
    }
    if (params.target.targetResourceId) {
      if (isNaN(params.target.targetResourceId)) {
        return this.generateNotSuccessRouteResult(null, StartTargetResultEnum.NotFound);
      }
      targetSelected = true;
      payload.targetResourceId = params.target.targetResourceId;
    }
    if (params.target.targetQrCodeId) {
      if (!this.checkIsGuid(params.target.targetQrCodeId)) {
        return this.generateNotSuccessRouteResult(null, StartTargetResultEnum.NotFound);
      }
      targetSelected = true;
      payload.targetQrCodeId = params.target.targetQrCodeId;
    }

    if (startSelected && targetSelected) {
      if (params.startSelectionMode == null) {
        payload.startType = StartTargetSelectionModeEnum.Unchanged;
      }
      if (params.targetSelectionMode == null) {
        payload.targetType = StartTargetSelectionModeEnum.Unchanged;
      }
    }

    const routeId = localStorage.getItem(ROUTE_ID_KEY);

    if (routeId && routeId != '') {
      payload.routeId = routeId;
    }

    return this.apiService.call<any, FindPathResponseV2>({ url: url, method: 'post', payload: payload }).pipe(
      tap((x) => {
        if (x.routeId) {
          localStorage.setItem(ROUTE_ID_KEY, x.routeId);
        }
      }),
      map((r) => {
        if (r.path != null) {
          const calculatedPathResponse = this.calculatePaths(r);

          if (params.wantOnlyBarrierFreePaths && !r.barrierFree) {
            r.pathResult = {
              ...r.pathResult,
              path: PathInfoResultEnum.FoundOnlyNotBarrierPath,
            };
          }

          return {
            directions: calculatedPathResponse.directions,
            responseType: calculatedPathResponse.response,
            response: {
              ...r,
              pathInstructions: r.pathInstructions.map((x) => {
                return {
                  ...x,
                  notShownOnMap: this.allPathItemsIsHideInNavigation(r.path, x.instructions),
                };
              }),
              targetGUID: params.target.targetQrCodeId,
              targetResourceId: params.target.targetResourceId,
            },
          };
        } else {
          if (!startSelected && !targetSelected) {
            r.pathResult = {
              ...r.pathResult,
              path: PathInfoResultEnum.PathFound,
              start: StartTargetResultEnum.NotRequested,
              target: StartTargetResultEnum.NotRequested,
            };
          }
        }
        return { directions: [], responseType: null, response: r };
      }),
      map((r) => ({
        loading: false,
        data: r,
        error: null,
      })),
      startWith({ loading: true, data: null, error: null }),
      catchError((e) =>
        of({
          loading: false,
          data: null,
          error: e,
        })
      )
    );
  }

  // Docasna implementace, nez se to udela jako on demand
  // BASIC LIST
  #kioskId = signal<undefined | string | null>(undefined);
  #appId = signal<undefined | string | null>(undefined);

  // Categories LIST
  #categoriesList = cuePackResource({
    params: () => {
      if (!this.#kioskId() && !this.#appId()) {
        return null;
      }
      return {
        kioskId: this.#kioskId(),
        appId: this.#appId(),
      };
    },
    stream: ({ params }) => this.getCategoriesList(params),
  });

  getCategories(params: HasAppId | HasKioskId) {
    if (hasAppId(params) && params.appId) {
      this.#appId.set(params.appId);
    } else if (hasKioskId(params) && params.kioskId) {
      this.#kioskId.set(params.kioskId);
    }
    return this.#categoriesList;
  }
}
